import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Send, Handshake } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { BUSINESS_TYPES } from "../../lib/data";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Reveal, Overline } from "../ui-kit";

const EMPTY = {
  full_name: "", company_name: "", business_type: "", email: "",
  phone: "", country: "", state: "", city: "", message: "",
};

const inputCls =
  "w-full bg-black/50 border border-zinc-800 text-white text-sm px-4 py-3 rounded-sm placeholder:text-zinc-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none transition-colors";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.business_type || !form.email || !form.phone) {
      toast.error("Please fill in name, business type, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      setSuccess(true);
      setForm(EMPTY);
      toast.success("Enquiry sent! Check your inbox for confirmation.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="dealers" className="relative py-24 md:py-32 bg-[#050505] overflow-hidden" data-testid="contact-section">
      <div className="absolute right-0 top-1/4 h-[500px] w-[500px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,90,0,0.12) 0%, transparent 70%)" }} />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          {/* Left: Dealer program pitch */}
          <div className="lg:sticky lg:top-28">
            <Overline index="07">Partner With Sobtrex</Overline>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
              Become a <span className="text-orange-500">Dealer</span>
            </h2>
            <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-lg">
              We are actively building our nationwide dealer and distributor network. If you're
              interested in representing Sobtrex in your region, we'd love to connect with you.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { icon: Handshake, t: "Long-Term Partnership", d: "Grow with a brand built for the long run." },
                { icon: CheckCircle2, t: "Full Product Portfolio", d: "Corded, cordless & the 20V platform." },
                { icon: Send, t: "Priority Launch Access", d: "Be first when Sobtrex goes live." },
              ].map((it, i) => (
                <Reveal key={i} delay={i * 0.08} className="flex items-start gap-4">
                  <div className="h-11 w-11 shrink-0 flex items-center justify-center border border-zinc-800 text-orange-500">
                    <it.icon size={20} />
                  </div>
                  <div>
                    <p className="font-display text-base font-bold uppercase text-white">{it.t}</p>
                    <p className="text-sm text-zinc-500">{it.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Right: glassmorphism form */}
          <Reveal id="contact">
            <div className="relative backdrop-blur-xl bg-[#18181a]/60 border border-white/10 p-7 md:p-9 shadow-2xl clip-notch">
              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="py-16 text-center"
                    data-testid="contact-success"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                      className="mx-auto h-20 w-20 rounded-full bg-orange-500/10 border border-orange-500/40 flex items-center justify-center text-orange-500"
                    >
                      <CheckCircle2 size={44} />
                    </motion.div>
                    <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">Request Received</h3>
                    <p className="mt-3 text-zinc-400 max-w-sm mx-auto">
                      Thank you for reaching out. Our team will get back to you shortly. A confirmation
                      email is on its way.
                    </p>
                    <button
                      onClick={() => setSuccess(false)}
                      className="mt-8 text-sm font-bold uppercase tracking-wider text-orange-500 hover:text-orange-400"
                      data-testid="contact-reset-btn"
                    >
                      Send another enquiry →
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4"
                    data-testid="contact-form"
                  >
                    <h3 className="font-display text-xl font-bold uppercase text-white mb-2">Request Information</h3>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <input className={inputCls} placeholder="Full Name *" value={form.full_name} onChange={set("full_name")} data-testid="input-full-name" />
                      <input className={inputCls} placeholder="Company Name" value={form.company_name} onChange={set("company_name")} data-testid="input-company" />
                    </div>

                    <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
                      <SelectTrigger className={`${inputCls} h-auto`} data-testid="select-business-type">
                        <SelectValue placeholder="Business Type *" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#101012] border-zinc-800 text-white">
                        {BUSINESS_TYPES.map((b) => (
                          <SelectItem key={b} value={b} data-testid={`business-type-${b.toLowerCase().replace(/\s/g, "-")}`}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <input type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} data-testid="input-email" />
                      <input className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="input-phone" />
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <input className={inputCls} placeholder="Country" value={form.country} onChange={set("country")} data-testid="input-country" />
                      <input className={inputCls} placeholder="State" value={form.state} onChange={set("state")} data-testid="input-state" />
                      <input className={inputCls} placeholder="City" value={form.city} onChange={set("city")} data-testid="input-city" />
                    </div>

                    <textarea rows={4} className={`${inputCls} resize-none`} placeholder="Message" value={form.message} onChange={set("message")} data-testid="input-message" />

                    <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                      className="w-full mt-2 bg-[#ff5a00] text-black font-display text-sm font-bold uppercase tracking-wide py-4 hover:bg-[#e04f00] hover:shadow-[0_0_40px_-8px_rgba(255,90,0,0.7)] transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                      {loading ? "Sending..." : <>Request Information <Send size={16} /></>}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
